import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { CheckCircle2, MessageCircle, MapPin, ArrowRight, Home, Building2, PackageCheck, Sparkles, Truck } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { StickyCTA } from "@/components/StickyCTA";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CtaBanner } from "@/components/CtaBanner";
import { MediaPanel } from "@/components/MediaPanel";
import { getService, getArea, areas, SITE_URL } from "@/data/seo";
import { waLink } from "@/lib/site";

const ICONS = {
  "house-removals": Home,
  "office-removals": Building2,
  "packing": PackageCheck,
  "end-of-tenancy-cleaning": Sparkles,
  "man-and-van": Truck,
};

// Programmatic page: /services/:serviceSlug/:areaSlug
export const ServiceAreaPage = () => {
  const { serviceSlug, areaSlug } = useParams();
  const svc = getService(serviceSlug);
  const a = getArea(areaSlug);
  if (!svc || !a) return <Navigate to="/" replace />;

  const canonical = `/services/${svc.slug}/${a.slug}`;
  const title = `${svc.shortName} ${a.name} | Trusted Movers — BoxB Solutions`;
  const description = `${svc.shortName} in ${a.name} by BoxB Solutions. Fully insured, fixed pricing, same-day quotes on WhatsApp. Serving ${a.landmarks.slice(0,3).join(", ")} and all of ${a.name}.`;

  const jsonld = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${svc.name} in ${a.name}`,
    description,
    serviceType: svc.name,
    provider: {
      "@type": "MovingCompany",
      name: "BoxB Solutions",
      telephone: "+447570291363",
      email: "info@boxb.uk",
      address: { "@type": "PostalAddress", addressLocality: "Cardiff", addressRegion: "South Wales", addressCountry: "GB" },
    },
    areaServed: { "@type": "City", name: a.name, containedInPlace: { "@type": "AdministrativeArea", name: "South Wales" } },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "127" },
    url: `${SITE_URL}${canonical}`,
  };

  const relatedAreas = Object.values(areas).filter((x) => x.slug !== a.slug).slice(0, 6);

  return (
    <main className="bg-white min-h-screen">
      <Seo title={title} description={description} canonical={canonical} jsonld={jsonld} />
      <Nav />

      <section className="pt-28 md:pt-32 pb-12">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Breadcrumbs trail={[
            { href: "/", label: "Home" },
            { href: "/services", label: "Services" },
            { href: `/services/${svc.slug}`, label: svc.shortName },
            { href: canonical, label: a.name },
          ]} />

          <div className="mt-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-bold text-[#2563EB]">
                <MapPin className="h-4 w-4" /> {a.name}
              </span>
              <h1 className="mt-4 font-display font-bold text-[#0A192F] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                {svc.shortName} in {a.name}
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                Looking for reliable {svc.shortName.toLowerCase()} in {a.name}? BoxB Solutions moves {a.name} residents and businesses every week. {svc.lead}
              </p>

              <ul className="mt-8 space-y-3">
                {svc.highlights.slice(0, 4).map((h) => (
                  <li key={h} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#2563EB] mt-0.5 shrink-0" />
                    <span className="text-slate-700">{h}</span>
                  </li>
                ))}
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#2563EB] mt-0.5 shrink-0" />
                  <span className="text-slate-700">Local {a.name} team who know the streets — including {a.landmarks.slice(0,2).join(" and ")}</span>
                </li>
              </ul>

              <div className="mt-9 flex flex-wrap gap-3">
                <a href={waLink(`Hi BoxB, I'd like a ${svc.shortName.toLowerCase()} quote in ${a.name} please.`)}
                   target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-7 py-4 font-semibold hover:brightness-95">
                  <MessageCircle className="h-4 w-4" /> WhatsApp for {a.name} quote
                </a>
                <Link to="/#contact"
                      className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white px-7 py-4 font-semibold hover:bg-[#0F172A]">
                  Get free quote
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              {svc.image ? (
                <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 shadow-[0_20px_60px_rgb(10,25,47,0.08)] h-[420px]">
                  <img src={svc.image} alt={`${svc.name} in ${a.name}, South Wales by BoxB Solutions`} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/45 via-transparent to-transparent" />
                  <div className="absolute left-6 bottom-6 rounded-2xl bg-white/95 backdrop-blur px-4 py-3">
                    <div className="text-xs uppercase tracking-widest font-bold text-[#2563EB]">Serving</div>
                    <div className="font-display font-semibold text-[#0A192F]">{a.name}</div>
                  </div>
                </div>
              ) : (
                <MediaPanel
                  variant={`${svc.slug}-${a.slug}`}
                  icon={ICONS[svc.slug]}
                  label={`${svc.shortName} · ${a.name}`}
                  height="h-[420px]"
                  className="shadow-[0_20px_60px_rgb(10,25,47,0.08)]"
                />
              )}
              <div className="mt-6 rounded-3xl bg-[#F8FAFC] border border-slate-200 p-6">
                <h2 className="font-display font-semibold text-lg text-[#0A192F]">Coverage in {a.name}</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {a.landmarks.map((l) => (
                    <span key={l} className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-3 py-1 text-xs text-[#0A192F]">
                      <MapPin className="h-3 w-3 text-[#2563EB]" /> {l}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <h2 className="font-display font-semibold text-3xl text-[#0A192F] tracking-tight">Why {a.name} chooses BoxB</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {svc.faqs.slice(0, 2).map((f) => (
              <div key={f.q} className="rounded-3xl bg-white border border-slate-200 p-7">
                <h3 className="font-display font-semibold text-lg text-[#0A192F]">{f.q}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <h2 className="font-display font-semibold text-2xl text-[#0A192F]">{svc.shortName} in nearby areas</h2>
          <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {relatedAreas.map((r) => (
              <Link key={r.slug} to={`/services/${svc.slug}/${r.slug}`}
                    className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center justify-between hover:border-[#0A192F] transition-colors">
                <span className="font-medium text-[#0A192F]">{svc.shortName} {r.name}</span>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner title={`Book your ${a.name} ${svc.shortName.toLowerCase()} today`} subtitle="Free quote in 15 minutes on WhatsApp. Fully insured, transparent pricing." />

      <Footer />
      <StickyCTA />
    </main>
  );
};
